import { apiRequest } from './client';
import type {
  AdminActivityDto,
  AdminDashboardDto,
  AdminEarningsSeriesPoint,
  AdminLoginResponse,
  AppVersionConfig,
  CodePublicDto,
  CodeWithCreatorDto,
  CreateCreatorResponse,
  CreatorActivityRowDto,
  CreatorPublicDto,
  CreatorWithStatsDto,
  EarningsSeriesPoint,
  PaginatedSubscribers,
  PayoutPublicDto,
  PayoutWithCreatorDto,
  SurveyAdminResult,
} from './types';

export const adminApi = {
  // ---------- auth ----------
  login(username: string, password: string): Promise<AdminLoginResponse> {
    return apiRequest('/admin/auth/login', {
      method: 'POST',
      body: { username, password },
    });
  },

  // ---------- app version gate ----------
  appVersion(): Promise<AppVersionConfig> {
    return apiRequest('/admin/app-version');
  },

  updateAppVersion(body: { minVersion: string; latestVersion: string; message: string | null }): Promise<AppVersionConfig> {
    return apiRequest('/admin/app-version', { method: 'PUT', body });
  },

  // ---------- dashboard ----------
  dashboard(): Promise<AdminDashboardDto> {
    return apiRequest('/admin/dashboard');
  },

  earningsSeries(months = 12): Promise<AdminEarningsSeriesPoint[]> {
    return apiRequest('/admin/earnings/series', { query: { months } });
  },

  activity(limit = 30): Promise<AdminActivityDto[]> {
    return apiRequest('/admin/activity', { query: { limit } });
  },

  // ---------- creators ----------
  listCreators(status?: 'active' | 'suspended'): Promise<CreatorWithStatsDto[]> {
    return apiRequest('/admin/creators', { query: { status } });
  },

  creator(id: number): Promise<CreatorWithStatsDto> {
    return apiRequest(`/admin/creators/${id}`);
  },

  creatorCodes(id: number): Promise<CodePublicDto[]> {
    return apiRequest(`/admin/creators/${id}/codes`);
  },

  creatorEarningsSeries(
    id: number,
    range: '3M' | '6M' | '12M' | 'all' = '12M',
  ): Promise<EarningsSeriesPoint[]> {
    return apiRequest(`/admin/creators/${id}/earnings/series`, { query: { range } });
  },

  creatorPayouts(id: number): Promise<PayoutPublicDto[]> {
    return apiRequest(`/admin/creators/${id}/payouts`);
  },

  creatorActivity(id: number, limit = 30): Promise<CreatorActivityRowDto[]> {
    return apiRequest(`/admin/creators/${id}/activity`, { query: { limit } });
  },

  creatorSubscribers(
    id: number,
    q: {
      status?: 'all' | 'active' | 'paused' | 'churned';
      code?: string;
      q?: string;
      page?: number;
      pageSize?: number;
    } = {},
  ): Promise<PaginatedSubscribers> {
    return apiRequest(`/admin/creators/${id}/subscribers`, { query: q as Record<string, any> });
  },

  createCreator(input: {
    email: string;
    handle: string;
    name: string;
    country?: string;
    commissionBasisPoints?: number;
    primaryCode?: string;
  }): Promise<CreateCreatorResponse> {
    return apiRequest('/admin/creators', { method: 'POST', body: input });
  },

  updateCreator(
    id: number,
    input: {
      paywallDisplayName?: string | null;
      paywallImageBase64?: string;
      clearPaywallImage?: boolean;
    },
  ): Promise<CreatorPublicDto> {
    return apiRequest(`/admin/creators/${id}`, { method: 'PATCH', body: input });
  },

  suspendCreator(id: number): Promise<CreatorPublicDto> {
    return apiRequest(`/admin/creators/${id}/suspend`, { method: 'POST', body: {} });
  },

  reactivateCreator(id: number): Promise<CreatorPublicDto> {
    return apiRequest(`/admin/creators/${id}/reactivate`, { method: 'POST', body: {} });
  },

  resetCreatorPassword(id: number): Promise<{ tempPassword: string }> {
    return apiRequest(`/admin/creators/${id}/reset-password`, { method: 'POST', body: {} });
  },

  // ---------- codes (review queue) ----------
  listCodes(approval?: 'approved' | 'pending' | 'rejected'): Promise<CodeWithCreatorDto[]> {
    return apiRequest('/admin/codes', { query: { approval } });
  },

  approveCode(id: number, notes?: string): Promise<CodePublicDto> {
    return apiRequest(`/admin/codes/${id}/approve`, { method: 'POST', body: { notes } });
  },

  rejectCode(id: number, reason: string): Promise<CodePublicDto> {
    return apiRequest(`/admin/codes/${id}/reject`, { method: 'POST', body: { reason } });
  },

  // ---------- payouts ----------
  listPayouts(
    status?: 'scheduled' | 'processing' | 'paid' | 'failed' | 'skipped',
  ): Promise<PayoutWithCreatorDto[]> {
    return apiRequest('/admin/payouts', { query: { status } });
  },

  runPayouts(): Promise<{ created: number; skipped: number; alreadyExisted: number }> {
    return apiRequest('/admin/payouts/run', { method: 'POST', body: {} });
  },

  markPayoutPaid(
    id: number,
    body: { notes?: string; receiptUrl?: string },
  ): Promise<PayoutPublicDto> {
    return apiRequest(`/admin/payouts/${id}/mark-paid`, { method: 'POST', body });
  },

  // ---------- feature requests ("What to build next?") ----------
  listFeatureRequests(status?: FeatureRequestStatus): Promise<FeatureRequestAdminDto[]> {
    return apiRequest('/admin/feature-requests', { query: { status } });
  },

  setFeatureRequestStatus(
    id: number,
    status: 'approved' | 'rejected' | 'planned' | 'completed',
  ): Promise<{ id: number; status: string }> {
    return apiRequest(`/admin/feature-requests/${id}`, { method: 'PATCH', body: { status } });
  },

  deleteFeatureRequest(id: number): Promise<{ id: number; deleted: boolean }> {
    return apiRequest(`/admin/feature-requests/${id}`, { method: 'DELETE' });
  },

  // ---------- surveys / in-app feedback ----------
  listSurveyResponses(
    filters: {
      survey_key?: string;
      sentiment?: 'up' | 'down';
      app_version?: string;
      from?: string;
      to?: string;
      limit?: number;
    } = {},
  ): Promise<SurveyAdminResult> {
    return apiRequest('/admin/surveys', { query: filters as Record<string, any> });
  },

  // ---------- web push (PWA notifications) ----------
  getVapidKey(): Promise<{ publicKey: string; enabled: boolean }> {
    return apiRequest('/admin/push/vapid-public-key');
  },

  subscribePush(sub: { endpoint: string; keys: { p256dh: string; auth: string } }): Promise<{ subscribed: boolean }> {
    return apiRequest('/admin/push/subscribe', { method: 'POST', body: sub });
  },

  unsubscribePush(endpoint: string): Promise<{ subscribed: boolean }> {
    return apiRequest('/admin/push/subscribe', { method: 'DELETE', body: { endpoint } });
  },

  sendTestPush(): Promise<{ sent: boolean }> {
    return apiRequest('/admin/push/test', { method: 'POST', body: {} });
  },
};

export type FeatureRequestStatus =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'planned'
  | 'completed';

export interface FeatureRequestAdminDto {
  id: number;
  title: string;
  details: string | null;
  email: string | null;
  status: FeatureRequestStatus;
  voteCount: number;
  deviceId: string | null;
  createdAt: string;
  updatedAt: string;
}
